/**
 * The site of this page (11 §8.3, ADR 0049): the Worker's shell sets `<html data-site>`; under Vite (no shell) the host
 * decides (`tarock.localhost`, `tile.localhost`). One build serves both sites.
 */
import { t } from '@tarock/i18n';
import { SITES, type SiteConfig, type SiteId, siteForHost } from '@tarock/protocol/sites';

/** The site of a document: its `data-site`, else its host's. */
export function detectSite(root: { dataset: DOMStringMap }, host: string): SiteId {
  const marked = root.dataset['site'];
  return marked === 'tarock' || marked === 'tile' ? marked : siteForHost(host);
}

/** This page's site. */
export const SITE: SiteId = detectSite(document.documentElement, location.host);
// the theme tokens key off the attribute (`:root[data-site=tile]`), so set it in dev too
document.documentElement.dataset['site'] = SITE;

/** This page's site configuration. */
export const site: SiteConfig = SITES[SITE];

/** The `{site}` and `{games}` of strings that name the site (11 §8.3). */
export const siteParams = (): { site: string; games: string } => ({ site: site.name, games: t(`site.games.${SITE}`) });

/**
 * The home of another site in this page's environment (production, staging or the dev host with its port). Plain
 * `localhost` is tarock.game locally (the Worker's dev origin, 11 §8.2), so tile.game there is `tile.localhost`.
 */
export function siteHome(
  target: SiteId,
  from: SiteId = SITE,
  loc: Pick<Location, 'hostname' | 'port' | 'protocol'> = location,
): string {
  const here = SITES[from].hosts;
  const there = SITES[target].hosts;
  const name =
    loc.hostname === here.production
      ? there.production
      : loc.hostname === here.staging
        ? there.staging
        : loc.hostname.endsWith('.localhost') || (loc.hostname === 'localhost' && target !== from)
          ? there.dev
          : loc.hostname;
  return `${loc.protocol}//${name}${loc.port ? `:${loc.port}` : ''}/`;
}
